import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Sparkles, Plus, CheckCircle2, Loader2, Users, Search } from "lucide-react";
import { toast } from "sonner";
import FileUpload from "../components/FileUpload";
import { usePaginacao } from "../hooks/usePaginacao";
import { Paginacao } from "../components/Paginacao";
import { EmptyState, SkeletonTabela } from "../components/EmptyState";
import { ExportarBotao } from "../components/ExportarBotao";

const STATUS_COLOR = {
  em_analise: "#eab308",
  aprovado: "#3b82f6",
  reprovado: "#dc2626",
  efetivado: "#16a34a",
};

export default function Clientes() {
  const {
    itens: clientes, total, pagina, totalPaginas, limite,
    carregando, filtros, irParaPagina, setFiltro, recarregar,
  } = usePaginacao("/clientes", { limite: 20, filtrosIniciais: { q: "", status: "" } });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", cnpj_cpf: "", tipo: "PJ", email: "", telefone: "", endereco: "", origem: "site", socios: "", referencias: "" });
  const [submitting, setSubmitting] = useState(false);
  const [resumo, setResumo] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const submit = async () => {
    // Validação client-side com mensagens claras (antes era silencioso em prod)
    if (!form.nome.trim()) {
      toast.error("Informe o nome / razão social");
      return;
    }
    if (!form.cnpj_cpf.trim()) {
      toast.error("Informe o CNPJ ou CPF");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/clientes", form);
      toast.success(`Cliente "${data.nome}" cadastrado com sucesso`);
      setOpen(false);
      setForm({ nome: "", cnpj_cpf: "", tipo: "PJ", email: "", telefone: "", endereco: "", origem: "site", socios: "", referencias: "" });
      recarregar();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg = "Falha ao cadastrar cliente";
      if (status === 409) msg = detail || "CNPJ/CPF já cadastrado";
      else if (status === 400) msg = detail || "Dados inválidos";
      else if (status === 422) {
        const errors = e?.response?.data?.detail;
        if (Array.isArray(errors)) {
          msg = errors.map((er) => `${er.loc?.slice(-1)[0]}: ${er.msg}`).join(" · ");
        }
      } else if (detail) msg = detail;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const parecer = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post("/ai/parecer-risco", { cliente_id: c.id });
      toast.success(`Parecer: ${data.parecer}`);
      recarregar();
    } finally {
      setBusyId(null);
    }
  };

  const efetivar = async (c) => {
    try {
      await api.post(`/clientes/${c.id}/efetivar`);
      toast.success(`${c.nome} efetivado! Cadastro ativado no LAB Locativo.`);
      recarregar();
    } catch (e) {
      toast.error("Falha ao efetivar");
    }
  };

  const resumoCli = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post("/ai/resumo-cliente", { cliente_id: c.id });
      setResumo({ cliente: c.nome, texto: data.resumo });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Clientes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total.toLocaleString("pt-BR")} clientes · cadastro unificado · parecer IA · efetivação para o LAB Locativo
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <ExportarBotao endpoint="/export/clientes" filtros={filtros} nomeArquivo="clientes" testId="exportar-clientes" />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-cliente-btn" className="text-white" style={{ background: "#F97316" }}>
              <Plus size={14} className="mr-2" /> Novo cliente
            </Button>
          </DialogTrigger>          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Cadastrar cliente</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nome / Razão Social <span className="text-red-500">*</span></Label><Input data-testid="cli-form-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
              <div><Label>CNPJ/CPF <span className="text-red-500">*</span></Label><Input data-testid="cli-form-doc" value={form.cnpj_cpf} onChange={(e) => setForm({ ...form, cnpj_cpf: e.target.value })} /></div>
              <div>
                <Label>Tipo</Label>
                <select
                  data-testid="cli-form-tipo"
                  className="w-full h-9 px-2 border rounded-md text-sm bg-white"
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                >
                  <option value="PJ">Pessoa Jurídica</option>
                  <option value="PF">Pessoa Física</option>
                </select>
              </div>
              <div className="col-span-2"><Label>Endereço</Label><Input value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} /></div>
              <div><Label>E-mail</Label><Input data-testid="cli-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Telefone</Label><Input data-testid="cli-form-tel" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
              <div className="col-span-2"><Label>Sócios</Label><Textarea value={form.socios} onChange={(e) => setForm({ ...form, socios: e.target.value })} /></div>
              <div className="col-span-2"><Label>Referências</Label><Textarea value={form.referencias} onChange={(e) => setForm({ ...form, referencias: e.target.value })} /></div>
            </div>
            <Button data-testid="cli-form-submit" disabled={submitting} onClick={submit} className="w-full mt-2 text-white" style={{ background: "#1B3A6B" }}>
              {submitting ? <><Loader2 className="animate-spin mr-2 inline" size={14} />Salvando…</> : "Cadastrar"}
            </Button>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Barra de filtros */}
      <Card className="p-3 flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Buscar por nome ou CNPJ/CPF…"
            value={filtros.q || ""}
            onChange={(e) => setFiltro("q", e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="clientes-busca"
          />
        </div>
        <select
          value={filtros.status || ""}
          onChange={(e) => setFiltro("status", e.target.value)}
          className="h-9 px-3 text-sm border border-zinc-200 rounded-md bg-white"
          data-testid="clientes-filtro-status"
        >
          <option value="">Todos os status</option>
          <option value="em_analise">Em análise</option>
          <option value="aprovado">Aprovado</option>
          <option value="reprovado">Reprovado</option>
          <option value="efetivado">Efetivado</option>
        </select>
      </Card>

      {resumo && (
        <Card className="p-5 border-l-4" style={{ borderLeftColor: "#F97316" }} data-testid="resumo-cliente-output">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-bold">Resumo de {resumo.cliente}</h3>
            <Button size="sm" variant="ghost" onClick={() => setResumo(null)}>Fechar</Button>
          </div>
          <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">{resumo.texto}</pre>
        </Card>
      )}

      <Card>
        {carregando ? (
          <SkeletonTabela linhas={10} colunas={7} />
        ) : clientes.length === 0 ? (
          <EmptyState
            icone={Users}
            titulo="Nenhum cliente encontrado"
            descricao={filtros.q ? `Nenhum resultado para "${filtros.q}".` : "Cadastre o primeiro cliente para começar."}
            acaoBotao={!filtros.q ? "Novo cliente" : null}
            aoClicar={() => setOpen(true)}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>CNPJ/CPF</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Parecer IA</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientes.map((c) => (
                  <TableRow key={c.id} data-testid={`cliente-row-${c.id}`}>
                    <TableCell className="font-medium">{c.nome}</TableCell>
                    <TableCell className="text-xs">{c.cnpj_cpf}</TableCell>
                    <TableCell><Badge variant="outline">{c.origem || "—"}</Badge></TableCell>
                    <TableCell>
                      <Badge style={{ background: STATUS_COLOR[c.status], color: "white" }}>{c.status}</Badge>
                    </TableCell>
                    <TableCell className="min-w-[180px]">
                      <FileUpload
                        endpoint={`/api/clientes/${c.id}/documento`}
                        accept="application/pdf,image/jpeg,image/png"
                        maxSizeMB={10}
                        currentUrl={c.documento_url}
                        onChange={recarregar}
                        testId={`cli-doc-${c.id}`}
                        label="Documentos"
                      />
                    </TableCell>
                    <TableCell className="text-xs">
                      {c.parecer_ia ? (
                        <div>
                          <div className="font-semibold">{c.parecer_ia}</div>
                          <div className="text-muted-foreground line-clamp-2 max-w-[280px]">{c.parecer_justificativa}</div>
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Link to={`/app/clientes/${c.id}`}>
                        <Button size="sm" variant="ghost" data-testid={`abrir-cli-${c.id}`} title="Abrir cadastro">
                          Abrir
                        </Button>
                      </Link>
                      <Button data-testid={`parecer-btn-${c.id}`} size="sm" variant="outline" onClick={() => parecer(c)} disabled={busyId === c.id}>
                        {busyId === c.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => resumoCli(c)}>Resumo IA</Button>
                      {c.status !== "efetivado" && (
                        <Button data-testid={`efetivar-btn-${c.id}`} size="sm" className="text-white" style={{ background: "#16a34a" }} onClick={() => efetivar(c)}>
                          <CheckCircle2 size={12} className="mr-1" /> Efetivar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="px-4">
              <Paginacao
                pagina={pagina}
                totalPaginas={totalPaginas}
                total={total}
                limite={limite}
                irParaPagina={irParaPagina}
              />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
